import { useContext, useEffect, useMemo, useState } from "react";

import { ButtonsRight } from "../../../../interfaces";
import { ContentInputIA } from "./IAInput";
import { ContentOutputIA } from "./IAOutput";
import { PublicationContext } from "../../../../context/PublicationContext";

export interface IaMessages {
  role: "user" | "assistant";
  content: { type: "text"; text: string }[];
}

interface Props {
  openButtons: ButtonsRight;
  setOpenButtons: (button: ButtonsRight) => void;
}

export const IaChat = ({ openButtons, setOpenButtons }: Props) => {
  const [userInput, setUserInput] = useState<string>("");
  const [AllMessages, setAllMessages] = useState<IaMessages[]>([]);
  const [threadId, setThreadId] = useState<string>("");
  const [inputDisabled, setInputDisabled] = useState<boolean>(false);
  const [isLoadResponse, setIsLoadResponse] = useState<boolean>(false);
  const { publication } = useContext(PublicationContext);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState<number>(0);

  const isAssistant = publication?.asistenteKey?.startsWith("asst_");
  const isVectorStore = publication?.asistenteKey?.startsWith("vs_");

  const containerSize = useMemo<number>(() => {
    const content = document.getElementById("contentIA");
    return content?.scrollHeight || 0;
  }, []);

  const apiKey =
    "sk-proj-" +
    [
      process.env.REACT_APP_OPENAI_API_KEY1,
      process.env.REACT_APP_OPENAI_API_KEY2,
      process.env.REACT_APP_OPENAI_API_KEY3,
    ]
      .join("")
      .trim();

  const loadingMessages = [
    "Analizando tu solicitud...",
    "Buscando información precisa...",
    "Procesando datos relevantes...",
    "Redactando una respuesta clara...",
    "Finalizando los últimos detalles...",
  ];

  useEffect(() => {
    const content = document.getElementById("contentIA");
    if (content) content.scrollTop = containerSize;
  }, [containerSize]);

  useEffect(() => {
    if (!isLoadResponse) {
      setLoadingMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isLoadResponse]);

  // Crear thread SOLO si es assistants
  useEffect(() => {
    if (!isAssistant) return;

    const createThread = async () => {
      const res = await fetch("https://api.openai.com/v1/threads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
          "OpenAI-Beta": "assistants=v2",
        },
      });

      const data = await res.json();
      setThreadId(data.id);
    };

    createThread();
  }, [isAssistant]);

  const extractTextFromResponse = (data: any): string => {
    const message = data.output?.find((item: any) => item.type === "message");

    if (!message) return data.output_text || "Sin respuesta";

    const textBlock = message.content?.find(
      (c: any) => c.type === "output_text",
    );

    return textBlock?.text || data.output_text || "Sin respuesta";
  };

  const sendMessage = async (): Promise<void> => {
    if (!userInput.trim()) return;

    setIsLoadResponse(true);
    setInputDisabled(true);

    const newMessages: IaMessages[] = [
      ...AllMessages,
      {
        role: "user",
        content: [{ type: "text", text: userInput }],
      },
    ];

    setAllMessages(newMessages);
    setUserInput("");
    moveScrollEnd();

    try {
      // =========================
      // RESPONSES (VECTOR STORE)
      // =========================
      if (isVectorStore) {
        const response = await fetch("https://api.openai.com/v1/responses", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model: "gpt-4.1",
            input: userInput,
            tools: [
              {
                type: "file_search",
                vector_store_ids: [publication.asistenteKey],
              },
            ],
            instructions: `
              Responde de forma clara, útil y amigable utilizando únicamente la información disponible en los archivos proporcionados.

              Si la pregunta del usuario está directamente relacionada con el contenido, responde con una explicación clara y bien estructurada.

              Si la pregunta está parcialmente relacionada, responde con la información disponible y aclara qué parte no está cubierta en los archivos.

              Si la pregunta no está relacionada o no hay información suficiente, responde amablemente que no cuentas con esa información en los materiales disponibles.

              No inventes información ni uses conocimientos externos.
              `,
          }),
        });

        const data = await response.json();
        const text = extractTextFromResponse(data);

        setAllMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: [{ type: "text", text }],
          },
        ]);
      }

      // =========================
      // ASSISTANTS (LEGACY)
      // =========================
      if (isAssistant) {
        await fetch(`https://api.openai.com/v1/threads/${threadId}/messages`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
            "OpenAI-Beta": "assistants=v2",
          },
          body: JSON.stringify({
            role: "user",
            content: [{ type: "text", text: userInput }],
          }),
        });

        const run = await fetch(
          `https://api.openai.com/v1/threads/${threadId}/runs`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiKey}`,
              "OpenAI-Beta": "assistants=v2",
            },
            body: JSON.stringify({
              assistant_id: publication.asistenteKey,
            }),
          },
        ).then((r) => r.json());

        await handleValidateRun(run.id);
      }
    } catch (error) {
      console.error(error);

      setAllMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: [{ type: "text", text: "Error procesando la solicitud." }],
        },
      ]);
    }

    setIsLoadResponse(false);
    setInputDisabled(false);
    moveScrollEnd();
  };

  const handleValidateRun = async (
    idRun: string,
    retries = 15,
    delay = 3000,
  ) => {
    let attempts = 0;

    while (attempts < retries) {
      const res = await fetch(
        `https://api.openai.com/v1/threads/${threadId}/runs/${idRun}`,
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "OpenAI-Beta": "assistants=v2",
          },
        },
      );

      const data = await res.json();

      if (data.status === "completed") {
        await getMessages();
        break;
      }

      if (data.status === "failed") break;

      await new Promise((r) => setTimeout(r, delay));
      attempts++;
    }
  };

  const getMessages = async () => {
    const res = await fetch(
      `https://api.openai.com/v1/threads/${threadId}/messages`,
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "OpenAI-Beta": "assistants=v2",
        },
      },
    );

    const data = await res.json();

    const last = data.data?.[0];

    if (last) {
      const text = last.content?.[0]?.text?.value || "Sin respuesta";

      setAllMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: [{ type: "text", text }],
        },
      ]);
    }
  };

  const moveScrollEnd = () => {
    const content = document.getElementById("contentIA");
    if (content) {
      setTimeout(() => {
        content.scrollTop = content.scrollHeight;
      }, 500);
    }
  };

  return (
    <div
      id="option-10"
      className={`aside__content ${openButtons.optIA ? "active" : ""}`}
    >
      <div className="aside__content-header">
        <h3>Chat IA</h3>
        <button
          className="btn btn-rounded btn-rounded-transparent btn-close"
          onClick={() => setOpenButtons({ ...openButtons, optIA: false })}
        >
          <span className="btn__icon">
            <i className="icon icon-vpro-close"></i>
          </span>
        </button>
      </div>

      <div className="aside__content-data main-sb">
        <div className="ai-chat">
          <ContentOutputIA messages={AllMessages} />

          {isLoadResponse && (
            <div className="ai-chat__loader">
              <i className="icon icon-cog rotate"></i>
              <span>{loadingMessages[loadingMessageIndex]}</span>
            </div>
          )}

          <ContentInputIA
            onSubmit={sendMessage}
            userInput={userInput}
            setValueInput={setUserInput}
            disabledInput={inputDisabled}
          />
        </div>
      </div>
    </div>
  );
};
